// Portfolio API Types

export type Site = {
    id: string;
    name: string;
    owner: string;
    area: string;
    coordinates: [number, number];
    polygonCoordinates?: [number, number][][];
    metrics: {
        vegetation_health: number;
        biodiversity_index: number;
        connectivity: number;
        human_modification: number;
        land_cover: number;
    };
    country: string;
};

export type TransformedSite = Site & {
    polygonCoordinates: [number, number][][];
};

export type PredictedSpeciesList = {
    iucnRedListCategory: string;
    scientificName: string;
    commonName: string;
    siteCount: number;
    predictionCreatedAt: string;
    predictionLastUpdated: string;
};

export enum PortfolioFrameworks {
    CSRD = 'CSRD',
    TNFD = 'TNFD',
    GRI = 'GRI',
}

export type PredictedSpeciesSummary = {
    totalCount: number;
    lastPredictionRun: string;
    predictionRunHistory: {
        predictionRunDate: string;
    }[];
    redListSpecies: {
        count: number;
        siteCount: number;
        frameworks: PortfolioFrameworks[];
        threatenedCount: number;
        chartData: {
            iucnRedListCategory: string;
            count: number;
        }[];
        speciesList: PredictedSpeciesList[];
    };
    invasiveSpecies: {
        count: number;
        siteCount: number;
        frameworks: PortfolioFrameworks[];
        speciesList: PredictedSpeciesList[];
    };
};

export type LandCoverYearCoverage = {
    year: number;
    assetCount: number;
    complete: boolean;
};

export type PortfolioMetric = {
    name: string;
    label: string;
    frameworks: PortfolioFrameworks[];
    year?: number;
    data?: number;
    years?: number[];
    datum?: number[];

    // Land Cover
    metricWithTrend?: {
        [key: number]: {
            [key: string]: number;
        };
    };
    yearCoverage?: LandCoverYearCoverage[];
};

export type BiiSiteSummary = {
    intact: number;
    moderate_intactness: number;
    low_intactness: number;
    severely_degraded: number;
};

export type HmiSiteSummary = {
    very_high_modification: number;
    high_modification: number;
    medium_modification: number;
    low_modification: number;
    no_modification: number;
};

export type PortfolioSiteSummary = {
    numberSites: number;
    totalArea: number;
    ownerCount: number;
    countryCount: number;
    biiSiteSummary: BiiSiteSummary;
    hmiSiteSummary: HmiSiteSummary;
    iucnRedListSites: number;
    metrics: PortfolioMetric[];
    predictedSpeciesSummary?: PredictedSpeciesSummary;
    geometry: {
        coordinates: [number, number][][];
    };
    childrenIds: string[];
};

export type PortfolioPolygon = {
    polygon: {
        coordinates: [number, number][][];
    };
};

export type PortfolioMarker = {
    marker: {
        coordinates: [number, number];
    };
    polygon: {
        coordinates: [number, number][][];
    };
};

export type PortfolioSite = {
    marker: {
        coordinates: [number, number];
    };
    polygon: {
        coordinates: [number, number][][];
    };
    data: {
        sampleCount: number;
        metrics: {
            name: string;
            label: string;
            frameworks: PortfolioFrameworks[];
        }[];
    };
};

export type PortfolioSpecies = {
    scientificName: string;
    commonName: string;
    confidence: PortfolioSpeciesConfidence;
    iucnRedListCategory: IUCNRedListCategory;
    siteCount: number;
};

export type PortfolioSpeciesConfidence = 'UNKNOWN' | 'LOW' | 'MEDIUM' | 'HIGH';
export type IUCNRedListCategory = 'UNKNOWN' | 'LC' | 'NT' | 'VU' | 'EN' | 'CR' | 'EW' | 'EX';

export type PortfolioAsset = {
    id: string;
    name: string;
    owner: string;
    area: number;
    country: string;
    BiodiversityIntactnessIndex: number;
    HumanModificationIndex: number;
    Primary_Productivity: number;
    Land_Cover: number;
    All_Connectivity: number;
    species: PortfolioSpecies[];
};

export type PortfolioFilters = {
    sites: string[];
    years: number[];
    availableMetrics: string[];
};

export type PortfolioSiteMetric = {
    name: string;
    label: string;
    frameworks: PortfolioFrameworks[];
    year?: number;
    data?: number;
    years?: number[];
    datum?: number[];
    metricWithTrend?: Record<string, number>;
    yearCoverage?: LandCoverYearCoverage[];
};
